import React from "react";
import { classArr } from "../helper/style-helper";
import { StyleableIcon } from "../helper/icon-helper";
import * as styles from "./section-sidebar.module.scss";

export interface SectionSidebarItem<Key extends string | number> {
	key: Key;
	label: string;
	icon: StyleableIcon;
}

interface SectionSidebarProps<Key extends string | number> {
	style?: React.CSSProperties;
	className?: string;
	items: SectionSidebarItem<Key>[];
	selectedKey: Key;
	onSelectKey: (key: Key) => void;
}

/**
 * SectionSidebar is a vertical list of icon-and-label items used to
 * navigate between sections of a page, such as the pages of a settings
 * screen.
 *
 * SectionSidebar uses item keys to separate item ordering and
 * visibility from business logic.
 *
 * @param props.items The ordered list of sections to display.
 * @param props.selectedKey The key of the currently active section.
 * @param props.onSelectKey A callback invoked when a section is clicked.
 */
export default function SectionSidebar<Key extends string | number>(
	props: SectionSidebarProps<Key>,
) {
	return (
		<div
			style={props.style}
			className={classArr(styles.container, props.className)}
		>
			{props.items.map((item) => {
				const isSelected = item.key === props.selectedKey;

				return (
					<button
						key={item.key}
						type="button"
						className={classArr(
							styles.item,
							isSelected && styles.itemSelected,
						)}
						onClick={() => props.onSelectKey(item.key)}
					>
						{item.icon(24, "currentColor", styles.icon)}

						<span className={styles.label}>{item.label}</span>
					</button>
				);
			})}
		</div>
	);
}
